import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect, useRef, useState,
} from 'react';

import { ScrollIndicator } from '@powdr/components';
import { LeaderboardDisplayMap, LeaderboardStatTypes } from '@powdr/constants';
import { useIsInView, useLeaderboard } from '@powdr/hooks';
import {
  getNumberWithCommas,
  toProperCase,
} from '@powdr/utils';

import {
  LeaderboardTable, StyledWearlynxLeaderboard,
  HeaderCell, StatCell, NameCell, Row,
  Rank, Name,
  ProfilePicture, ProfilePicturePlaceholder, ProfilePictureWrapper,
  TableTitle,
  TableHeader, TableBody,
  HeaderWrapper,
  ScrollableContainer,
} from './styles';

export const WearlynxLeaderboard = ({
  className = '',
  tableTitle,
  timeframe,
  stat,
  isWrapperless = false,
  enableUserScrolling = false,
  parentColorProfile,
}) => {
  const scrollRef = useRef(null);
  const isBodyInView = useIsInView(scrollRef);
  const [fading, setFading] = useState(false);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(!enableUserScrolling);

  const leaderboardData = useLeaderboard(
    timeframe,
    stat,
  );
  const scrollDuration = 45000; // milliseconds
  const fadeDuration = 1000; // milliseconds
  const pauseBeforeRestart = 3000; // milliseconds
  const startDelay = 3000;// milliseconds

  const getFormattedStat = (s) => {
    if (stat === LeaderboardStatTypes.VERTICAL_FEET || stat === LeaderboardStatTypes.LINEAR_FEET) {
      return `${getNumberWithCommas(s)}'`;
    }

    return s;
  };
  useEffect(() => {
    if (
      !leaderboardData?.length
    || !scrollRef.current
    || enableUserScrolling
    ) return;

    // Start animation only if in view and not started yet
    if (isBodyInView || !hasAnimationStarted.current) {
      if (isBodyInView && !hasAnimationStarted.current) {
        setHasAnimationStarted(true);
      }

      const el = scrollRef.current;
      let animationFrameId;
      let fadeTimeoutId;
      let pauseTimeoutId;
      let startDelayTimeoutId;
      let animationStartTime = null;

      const maxScroll = el.scrollHeight - el.clientHeight;

      const step = (timestamp) => {
        if (!animationStartTime) animationStartTime = timestamp;
        const elapsed = timestamp - animationStartTime;
        const progress = Math.min(elapsed / scrollDuration, 1);
        el.scrollTop = maxScroll * progress;

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(step);
        } else {
          pauseTimeoutId = setTimeout(() => {
            setFading(true);

            fadeTimeoutId = setTimeout(() => {
              el.scrollTop = 0;
              setFading(false);
              animationStartTime = null;

              startDelayTimeoutId = setTimeout(() => {
                animationFrameId = requestAnimationFrame(step);
              }, startDelay);
            }, fadeDuration);
          }, pauseBeforeRestart);
        }
      };

      // Start animation after delay
      startDelayTimeoutId = setTimeout(() => {
        animationFrameId = requestAnimationFrame(step);
      }, startDelay);

      // eslint-disable-next-line consistent-return
      return () => {
        cancelAnimationFrame(animationFrameId);
        clearTimeout(fadeTimeoutId);
        clearTimeout(pauseTimeoutId);
        clearTimeout(startDelayTimeoutId);
      };
    }
  }, [leaderboardData, enableUserScrolling, isBodyInView, hasAnimationStarted]);

  const handlePointerDown = (e) => {
    if (scrollRef.current?.contains(e.target)) {
      scrollRef.current = true;
    }
  };

  const handleScroll = useCallback(() => {
    if (scrollRef.current && !hasUserScrolled) {
      setHasUserScrolled(true);
      scrollRef.current = false;
    }
  }, [hasUserScrolled]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    scrollEl.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      scrollEl.removeEventListener('scroll', handleScroll);
    };
  }, [hasUserScrolled, handleScroll]);

  return (
    <StyledWearlynxLeaderboard
      className={className}
      $isWrapperless={isWrapperless}
      $is5050={false}
    >
      <LeaderboardTable role="table">
        {/* Table Header */}
        <HeaderWrapper>
          {(tableTitle) && (
            <TableTitle
              $isWrapperless={isWrapperless}
              $colorProfile={parentColorProfile}
            >
              {tableTitle}
            </TableTitle>
          )}
          <TableHeader role="rowgroup" $colorProfile={parentColorProfile}>
            <Row role="row">
              <HeaderCell
                role="columnheader"
                $isWrapperless={isWrapperless}
                $colorProfile={parentColorProfile}
              >
                Rider
              </HeaderCell>
              <HeaderCell
                role="columnheader"
                $isWrapperless={isWrapperless}
                $alignRight
                $colorProfile={parentColorProfile}
              >
                {LeaderboardDisplayMap?.[stat]?.displayName}
              </HeaderCell>
            </Row>
          </TableHeader>
        </HeaderWrapper>

        {/* Scrollable Table Body */}
        <ScrollableContainer
          ref={scrollRef}
          $isWrapperless={isWrapperless}
          $fading={fading}
          $fadeDuration={fadeDuration}
          $disableScroll={!enableUserScrolling}
          onScroll={handleScroll}
        >
          <TableBody role="rowgroup">
            {leaderboardData?.map((entry, idx) => (
              <Row key={entry.guid} role="row" $isWrapperless={isWrapperless}>
                <NameCell role="cell" $isWrapperless={isWrapperless}>
                  <Rank $isWrapperless={isWrapperless}>{`${idx + 1}.`}</Rank>

                  <ProfilePictureWrapper
                    $colorProfile={parentColorProfile}
                    $isWrapperless={isWrapperless}
                  >
                    {entry.profile_photo === 1 ? (
                      <ProfilePicture src={`https://cdn.skilynx.com/user_profile_photos/${entry.guid}.jpg`} />
                    ) : (
                      <ProfilePicturePlaceholder
                        $colorProfile={parentColorProfile}
                        $isWrapperless={isWrapperless}
                      >
                        {`${entry.display.charAt(0)}${entry.last_initial}`}
                      </ProfilePicturePlaceholder>
                    )}
                  </ProfilePictureWrapper>

                  <Name $isWrapperless={isWrapperless}>{`${toProperCase(entry.display)} ${entry.last_initial}.`}</Name>
                </NameCell>
                <StatCell role="cell" $isWrapperless={isWrapperless}>{getFormattedStat(entry[stat])}</StatCell>
              </Row>
            ))}

          </TableBody>
        </ScrollableContainer>
        {enableUserScrolling && (
          <ScrollIndicator
            parentColorProfile={parentColorProfile}
            bottomOffset={15}
            loopAnimation
            fadeOut={hasUserScrolled}
          />
        )}
      </LeaderboardTable>
    </StyledWearlynxLeaderboard>
  );
};

WearlynxLeaderboard.propTypes = {
  className: PropTypes.string,
  tableTitle: PropTypes.string,
  timeframe: PropTypes.string.isRequired,
  stat: PropTypes.string.isRequired,
  parentColorProfile: PropTypes.string,
  isWrapperless: PropTypes.bool,
  enableUserScrolling: PropTypes.bool,
};
